import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';
import { useRecoilValue } from 'recoil';
import { P, match } from 'ts-pattern';

import { metadata2State } from '@/state/metadataState';
import { shopAuthContextState } from '@/state/userAuthContext';

import { useGetSaleTaskSummaries } from './useGetSaleTaskSummaries';
import { useInfiniteQueryGetNotifications } from './useInfiniteQueryGetNotifications';

type ConvertedNotification = {
  notificationID: string;
  notificationType: 'order' | 'sale' | 'platform' | undefined;
  notificationCode: string;
  createDate: string;
  isFailure: boolean;
  totalCount: number;
  successCount: number;
  failureCount: number;
  actionText: string;
  errorCode: string;
  errorMessage: string;
};

type ConvertedSummary = {
  saleTaskID: string;
  totalCount: number;
  actionText: string;
  successCount: number;
  createDate: string;
};

/**
 * NotificationDialogBtn에서 쓰는 데이터를 정제합니다.
 */
export default function useNotificationData(isOpen: boolean) {
  const shopInfo = useRecoilValue(shopAuthContextState);
  const metadata = useRecoilValue(metadata2State);

  const {
    data,
    hasNextPage,
    isFetching,
    fetchNextPage,
    isInitialLoading: isNotificationLoading,
  } = useInfiniteQueryGetNotifications({
    shopID: shopInfo.shopID,
    request: {
      isPolling: false,
      pageNumber: 1,
      pageSize: 100,
    },
    options: {
      enabled: isOpen && !isEmpty(shopInfo.shopID),
      refetchInterval: isOpen ? 2000 : 0,
    },
  });

  const { data: saleTaskSummaryList, isLoading: isSaleTaskSummaryLoading } = useGetSaleTaskSummaries({
    shopID: shopInfo.shopID,
    options: {
      enabled: isOpen && !isEmpty(shopInfo.shopID),
      refetchInterval: isOpen ? 2000 : 0,
    },
  });

  const actionOrderTypeMetadata = metadata['notification']?.['order-type'];
  const actionSaleTypeMetadata = metadata['notification']?.['sale-type'];
  const actionPlatformTypeMetadata = metadata['notification']?.['type'];

  const saleTaskActionMetadata = metadata['sale']?.['task-action'];

  const convertedNotificationList: ConvertedNotification[] =
    data?.pages
      .flatMap(({ data }) => data.notifications)
      .map(notification => {
        const notificationType = actionOrderTypeMetadata?.codeList?.includes(notification?.type ?? '')
          ? 'order'
          : actionSaleTypeMetadata?.codeList?.includes(notification?.type ?? '')
          ? 'sale'
          : actionPlatformTypeMetadata?.codeList?.includes(notification?.type ?? '')
          ? 'platform'
          : undefined;

        const targetMetadata =
          notificationType === 'order'
            ? actionOrderTypeMetadata
            : notificationType === 'sale'
            ? actionSaleTypeMetadata
            : notificationType === 'platform'
            ? actionPlatformTypeMetadata
            : undefined;

        return {
          notificationID: notification?.notificationID ?? '',
          notificationType: notificationType,
          notificationCode: notification?.type ?? '',
          createDate: dayjs(notification?.createDate).format('YYYY-MM-DD HH:mm:ss'),
          isFailure: notification?.additionalField?.failureCount > 0,
          totalCount: notification?.additionalField?.totalCount,
          successCount: notification?.additionalField?.successCount,
          failureCount: notification?.additionalField?.failureCount,
          actionText: match(notification?.type)
            .with(P.union('NOTIFICATION_TYPE_DELETE-PLATFROM-SUCCESS'), () => '연동 해제 성공했습니다.')
            .with(
              P.union('NOTIFICATION_ORDER-TYPE_CONFIRM-SUCCESS', 'NOTIFICATION_ORDER-TYPE_CONFIRM-ERROR'),
              () => '배송준비중으로',
            )
            .with(
              P.union(
                'NOTIFICATION_ORDER-TYPE_CONFIRM-SHIPPING-SUCCESS',
                'NOTIFICATION_ORDER-TYPE_CONFIRM-SHIPPING-ERROR',
              ),
              () => '배송중으로',
            )
            .otherwise(() => {
              return notification?.additionalField?.failureCount > 0
                ? (targetMetadata?.[notification?.type ?? ''] as string | undefined)?.split('실패')[0] ?? ''
                : (targetMetadata?.[notification?.type ?? ''] as string | undefined)?.split('성공')[0] ?? '';
            }),
          errorCode: notification?.additionalField?.errors?.[0]?.errorcode ?? '',
          errorMessage: notification?.additionalField?.errors?.[0]?.errormessage ?? '',
        };
      }) ?? [];

  const convertedSummary: ConvertedSummary[] =
    saleTaskSummaryList?.saleTaskSummaries?.map(summary => ({
      saleTaskID: summary.saleTaskID ?? '',
      totalCount: summary.totalCount ?? 0,
      actionText: match(summary.saleTaskAction)
        .with('SALE_TASK-ACTION_CONFIRM-ORDER', () => '배송준비중으로')
        .with('SALE_TASK-ACTION_CONFIRM-SHIPPING', () => '배송중으로')
        .with('SALE_TASK-ACTION_DELETE-ITEM', () => '상품 삭제')
        .otherwise(() => {
          return saleTaskActionMetadata?.[summary.saleTaskAction ?? ''] as string;
        }),
      successCount: summary.successCount ?? 0,
      createDate: dayjs(summary.createDate).format('YYYY-MM-DD HH:mm:ss'),
    })) ?? [];

  return {
    isDataLoading: isNotificationLoading || isSaleTaskSummaryLoading,
    notification: {
      data: convertedNotificationList,
      hasNextPage,
      isFetching,
      fetchNextPage,
      isNotificationLoading,
    },
    summary: {
      data: convertedSummary,
    },
  };
}
